import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button, Collapse, CircularProgress, IconButton, TextField, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AVAILABILITY, ENGINE_TYPES, STATUSES } from '../constants';
import { availabilityColors, statusColors } from '../colors';
import type {
  AvailabilityKey,
  DashboardFilterKey,
  EngineTypeKey,
  PickupKey,
  Site,
  StatusKey,
} from '../types';
import { FILTER_LABELS as LABELS } from '../../../utils/labels';
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined';
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import SpinnerButton from '../../../components/SpinnerButton';
import ActiveFilterChipsBar from './ActiveFilterChipsBar';

const ALL_AVAILABILITY_KEYS = Object.keys(AVAILABILITY) as AvailabilityKey[];
const ALL_STATUS_KEYS = Object.keys(STATUSES) as StatusKey[];
const ALL_ENGINE_TYPE_KEYS = Object.keys(ENGINE_TYPES) as EngineTypeKey[];
const SITES_VISIBLE = 4;
const EXPANDED_VISIBLE_COUNT = 10;

interface FilterPanelProps {
  search: string;
  setSearch: (v: string) => void;
  availabilityFilter: Set<AvailabilityKey>;
  toggleAvailability: (k: AvailabilityKey) => void;
  statusFilter: Set<StatusKey>;
  toggleStatus: (k: StatusKey) => void;
  engineTypeFilter: Set<EngineTypeKey>;
  toggleEngineType: (k: EngineTypeKey) => void;
  siteFilter: Set<string>;
  toggleSite: (id: string) => void;
  sites: Site[];
  supplierFilter: Set<string>;
  toggleSupplier: (id: string) => void;
  suppliers: { id: string; name: string }[];
  subcontractorFilter: Set<string>;
  toggleSubcontractor: (id: string) => void;
  subcontractors: { id: string; name: string }[];
  categoryFilter: Set<string>;
  toggleCategory: (category: string) => void;
  categories: string[];
  optionsFilter: Set<string>;
  toggleOption: (key: string) => void;
  options: { key: string; label: string }[];
  filteredCount: number;
  totalCount: number;
  onClose?: () => void;
  pickupFilter: Set<PickupKey>;
  togglePickup: (k: PickupKey) => void;
  dashboardFilter: DashboardFilterKey | null;
  onClearDashboardFilter: () => void;
  hideChips?: boolean;
  onSave?: () => void;
  onExport?: () => void;
  exporting?: boolean;
  pickupHandinEnabled?: boolean;
  committedFilters?: {
    availabilityFilter: Set<AvailabilityKey>;
    statusFilter: Set<StatusKey>;
    engineTypeFilter: Set<EngineTypeKey>;
    siteFilter: Set<string>;
    supplierFilter: Set<string>;
    subcontractorFilter: Set<string>;
    categoryFilter: Set<string>;
    optionsFilter: Set<string>;
    pickupFilter: Set<PickupKey>;
  };
}

function FilterButton({
  label,
  isActive,
  dotColor,
  onClick,
  testId,
}: {
  label: string;
  isActive: boolean;
  dotColor?: string;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <Button
      variant="text"
      onClick={onClick}
      aria-pressed={isActive}
      className="w-full flex justify-between items-center gap-2 text-sm"
      data-testid={testId}
    >
      <span className="flex items-baseline gap-2 grow pl-2">
        {dotColor && (
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: dotColor }}
          />
        )}
        <span className={`normal-case text-left ${isActive ? 'font-bold' : 'text-text-secondary'}`}>
          {label}
        </span>
      </span>
      {isActive && <CheckIcon className="shrink-0 text-primary" fontSize="small" />}
    </Button>
  );
}

function ExpandableFilterList({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  useLayoutEffect(() => {
    if (!expanded) {
      setMaxHeight(undefined);
      return;
    }
    const firstItem = scrollRef.current?.firstElementChild as HTMLElement | null;
    if (!firstItem) return;

    const measure = () => {
      const itemHeight = firstItem.getBoundingClientRect().height;
      setMaxHeight(itemHeight > 0 ? itemHeight * EXPANDED_VISIBLE_COUNT : undefined);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(firstItem);
    return () => observer.disconnect();
  }, [expanded]);

  if (!expanded) {
    return <>{children}</>;
  }

  return (
    <div ref={scrollRef} style={maxHeight ? { maxHeight } : undefined} className="overflow-y-auto">
      {children}
    </div>
  );
}

function CollapsibleFilterSection({
  title,
  expanded,
  onToggle,
  testId,
  children,
}: {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section className="py-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={testId}
        className="flex items-center mb-0 justify-between gap-1.5 w-full text-left bg-transparent border-0 cursor-pointer px-2"
      >
        <span
          className={`text-xs font-bold uppercase tracking-wider text-text-secondary mb-0 mt-0`}
        >
          {title}
        </span>
        <ExpandMoreIcon
          fontSize="small"
          className={`text-text-secondary shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
        />
      </button>
      <Collapse in={expanded}>{children}</Collapse>
    </section>
  );
}

export default function FilterPanel({
  search,
  setSearch,
  availabilityFilter,
  toggleAvailability,
  statusFilter,
  toggleStatus,
  engineTypeFilter,
  toggleEngineType,
  siteFilter,
  toggleSite,
  sites,
  supplierFilter,
  toggleSupplier,
  suppliers,
  subcontractorFilter,
  toggleSubcontractor,
  subcontractors,
  categoryFilter,
  toggleCategory,
  categories,
  optionsFilter,
  toggleOption,
  options,
  filteredCount,
  totalCount,
  onClose,
  pickupFilter,
  togglePickup,
  dashboardFilter,
  onClearDashboardFilter,
  hideChips = false,
  onSave,
  onExport,
  exporting,
  committedFilters,
  pickupHandinEnabled = false,
}: FilterPanelProps) {
  const [showAllSites, setShowAllSites] = useState(false);
  const [showAllSuppliers, setShowAllSuppliers] = useState(false);
  const [showAllSubcontractors, setShowAllSubcontractors] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);

  const [engineTypeExpanded, setEngineTypeExpanded] = useState(false);
  const [categoryExpanded, setCategoryExpanded] = useState(false);
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [locationExpanded, setLocationExpanded] = useState(false);
  const [subcontractorExpanded, setSubcontractorExpanded] = useState(false);
  const [supplierExpanded, setSupplierExpanded] = useState(false);

  const availabilityKeys = pickupHandinEnabled
    ? ALL_AVAILABILITY_KEYS
    : ALL_AVAILABILITY_KEYS.filter((k) => k !== 'reserved');
  const isAllAvailability = availabilityKeys.every((k) => availabilityFilter.has(k));
  const isAllStatus = ALL_STATUS_KEYS.every((k) => statusFilter.has(k));
  const isAllEngineType = ALL_ENGINE_TYPE_KEYS.every((k) => engineTypeFilter.has(k));
  const isAllSites = siteFilter.size === 0 || sites.every((s) => siteFilter.has(s.id));
  const isAllSuppliers =
    supplierFilter.size === 0 || suppliers.every((s) => supplierFilter.has(s.id));
  const isAllSubcontractors =
    subcontractorFilter.size === 0 || subcontractors.every((s) => subcontractorFilter.has(s.id));
  const isAllCategories =
    categoryFilter.size === 0 || categories.every((c) => categoryFilter.has(c));
  const isAllOptions = optionsFilter.size === 0 || options.every((o) => optionsFilter.has(o.key));
  const isAllPickup = (['confirmed', 'not_confirmed'] as PickupKey[]).every((k) =>
    pickupFilter.has(k)
  );
  const visibleSites = showAllSites ? sites : sites.slice(0, SITES_VISIBLE);
  const hiddenCount = sites.length - SITES_VISIBLE;
  const visibleSuppliers = showAllSuppliers ? suppliers : suppliers.slice(0, SITES_VISIBLE);
  const hiddenSupplierCount = suppliers.length - SITES_VISIBLE;
  const visibleSubcontractors = showAllSubcontractors
    ? subcontractors
    : subcontractors.slice(0, SITES_VISIBLE);
  const hiddenSubcontractorCount = subcontractors.length - SITES_VISIBLE;
  const visibleCategories = showAllCategories ? categories : categories.slice(0, SITES_VISIBLE);
  const hiddenCategoryCount = categories.length - SITES_VISIBLE;
  const visibleOptions = showAllOptions ? options : options.slice(0, SITES_VISIBLE);
  const hiddenOptionCount = options.length - SITES_VISIBLE;

  return (
    <div className="flex flex-col h-full">
      <div className="px-2.5 py-2 border-b border-border">
        <TextField
          value={search}
          size="small"
          onChange={(e) => setSearch(e.target.value)}
          placeholder={LABELS.searchPlaceholder}
          fullWidth
        />
      </div>

      {!hideChips && (
        <ActiveFilterChipsBar
          availabilityFilter={committedFilters?.availabilityFilter ?? availabilityFilter}
          toggleAvailability={committedFilters ? () => {} : toggleAvailability}
          statusFilter={committedFilters?.statusFilter ?? statusFilter}
          toggleStatus={committedFilters ? () => {} : toggleStatus}
          engineTypeFilter={committedFilters?.engineTypeFilter ?? engineTypeFilter}
          toggleEngineType={committedFilters ? () => {} : toggleEngineType}
          siteFilter={committedFilters?.siteFilter ?? siteFilter}
          toggleSite={committedFilters ? () => {} : toggleSite}
          sites={sites}
          supplierFilter={committedFilters?.supplierFilter ?? supplierFilter}
          toggleSupplier={committedFilters ? () => {} : toggleSupplier}
          suppliers={suppliers}
          subcontractorFilter={committedFilters?.subcontractorFilter ?? subcontractorFilter}
          toggleSubcontractor={committedFilters ? () => {} : toggleSubcontractor}
          subcontractors={subcontractors}
          categoryFilter={committedFilters?.categoryFilter ?? categoryFilter}
          toggleCategory={committedFilters ? () => {} : toggleCategory}
          categories={categories}
          optionsFilter={committedFilters?.optionsFilter ?? optionsFilter}
          toggleOption={committedFilters ? () => {} : toggleOption}
          options={options}
          pickupFilter={committedFilters?.pickupFilter ?? pickupFilter}
          togglePickup={committedFilters ? () => {} : togglePickup}
          dashboardFilter={dashboardFilter}
          className="px-2.5 py-2"
          pickupHandinEnabled={pickupHandinEnabled}
        />
      )}

      <div className="flex-1 overflow-hidden relative">
        {dashboardFilter && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
            <div className="rounded-xl border border-primary/25 bg-surface shadow-lg px-4 py-3.5 flex flex-col items-center text-center w-40 gap-2">
              {dashboardFilter === 'arriving' && (
                <>
                  <ArrowCircleDownOutlinedIcon color="primary" />
                  <p className="uppercase text-xs mb-0 mt-0">{LABELS.filterActive}</p>
                  <p className="text-sm font-semibold mt-0">{LABELS.dashboardFilterArriving}</p>
                </>
              )}
              {dashboardFilter === 'pickup_confirmed' && (
                <>
                  <ArrowCircleUpOutlinedIcon color="success" />
                  <p className="uppercase text-xs mb-0 mt-0">{LABELS.filterActive}</p>
                  <p className="text-sm font-semibold mt-0 ">
                    {LABELS.dashboardFilterPickupConfirmed}
                  </p>
                </>
              )}
              {dashboardFilter === 'pickup_action' && (
                <>
                  <WarningAmberIcon color="warning" />
                  <p className="uppercase text-xs mb-0 mt-0">{LABELS.filterActive}</p>
                  <p className="text-sm font-semibold mt-0 ">
                    {LABELS.dashboardFilterPickupAction}
                  </p>
                </>
              )}
              {dashboardFilter === 'to_order_action' && (
                <>
                  <ShoppingCartOutlinedIcon color="warning" />
                  <p className="uppercase text-xs mb-0 mt-0">{LABELS.filterActive}</p>
                  <p className="text-sm font-semibold mt-0 ">
                    {LABELS.dashboardFilterToOrderAction}
                  </p>
                </>
              )}
              {dashboardFilter === 'rental_extension' && (
                <>
                  <AutorenewIcon color="primary" />
                  <p className="uppercase text-xs mb-0 mt-0">{LABELS.filterActive}</p>
                  <p className="text-sm font-semibold mt-0 ">
                    {LABELS.dashboardFilterRentalExtension}
                  </p>
                </>
              )}
              {dashboardFilter === 'overdue_return' && (
                <>
                  <WarningAmberIcon color="error" />
                  <p className="uppercase text-xs mb-0 mt-0">{LABELS.filterActive}</p>
                  <p className="text-sm font-semibold mt-0 ">
                    {LABELS.dashboardFilterOverdueReturn}
                  </p>
                </>
              )}
              <Button
                onClick={onClearDashboardFilter}
                variant="outlined"
                size="small"
                color="inherit"
              >
                {LABELS.dashboardFilterReset}
              </Button>
            </div>
          </div>
        )}
        <div
          className={`px-1.5 py-2 h-full overflow-y-auto flex flex-col divide-y divide-border transition-all duration-200 ${dashboardFilter ? 'opacity-25 blur-[1.5px] pointer-events-none select-none' : ''}`}
        >
          <section className="py-2">
            <p className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-0 mt-0 px-2">
              {LABELS.availability}
            </p>
            <div className="mt-1">
              {(Object.entries(AVAILABILITY) as [AvailabilityKey, { label: string }][])
                .filter(([k]) => availabilityKeys.includes(k))
                .map(([k, v]) => (
                  <FilterButton
                    key={k}
                    label={v.label}
                    isActive={availabilityFilter.has(k) && !isAllAvailability}
                    dotColor={availabilityColors[k]}
                    onClick={() => toggleAvailability(k)}
                    testId={`filter-availability-${k}`}
                  />
                ))}
            </div>
          </section>

          <section className="py-2">
            <p className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-0 mt-0 px-2">
              {LABELS.status}
            </p>
            <div className="mt-1">
              {(Object.entries(STATUSES) as [StatusKey, { label: string }][]).map(([k, v]) => (
                <FilterButton
                  key={k}
                  label={v.label}
                  isActive={statusFilter.has(k) && !isAllStatus}
                  dotColor={statusColors[k].dot}
                  onClick={() => toggleStatus(k)}
                  testId={`filter-status-${k}`}
                />
              ))}
            </div>
          </section>

          <section className="py-2">
            <p className="text-xs font-bold uppercase tracking-wider text-text-secondary mb-1 mt-0 px-2">
              {LABELS.pickup}
            </p>
            <div className="mt-1">
              <FilterButton
                label={LABELS.pickupConfirmed}
                isActive={pickupFilter.has('confirmed') && !isAllPickup}
                onClick={() => togglePickup('confirmed')}
                testId="filter-pickup-confirmed"
              />
              <FilterButton
                label={LABELS.pickupNotConfirmed}
                isActive={pickupFilter.has('not_confirmed') && !isAllPickup}
                onClick={() => togglePickup('not_confirmed')}
                testId="filter-pickup-not_confirmed"
              />
            </div>
          </section>

          <CollapsibleFilterSection
            title={LABELS.engineType}
            expanded={engineTypeExpanded}
            onToggle={() => setEngineTypeExpanded((v) => !v)}
            testId="filter-section-toggle-engine-type"
          >
            <div className="mt-1">
              {(Object.entries(ENGINE_TYPES) as [EngineTypeKey, { label: string }][])
                .filter(([k]) => k !== 'none')
                .map(([k, v]) => (
                  <FilterButton
                    key={k}
                    label={v.label}
                    isActive={engineTypeFilter.has(k) && !isAllEngineType}
                    onClick={() => toggleEngineType(k)}
                    testId={`filter-engine-${k}`}
                  />
                ))}
            </div>
          </CollapsibleFilterSection>

          {categories.length > 1 && (
            <CollapsibleFilterSection
              title={LABELS.category}
              expanded={categoryExpanded}
              onToggle={() => setCategoryExpanded((v) => !v)}
              testId="filter-section-toggle-category"
            >
              <ExpandableFilterList expanded={showAllCategories}>
                {visibleCategories.map((category) => (
                  <div className="mt-1">
                    <FilterButton
                      key={category}
                      label={category}
                      isActive={categoryFilter.has(category) && !isAllCategories}
                      onClick={() => toggleCategory(category)}
                    />
                  </div>
                ))}
              </ExpandableFilterList>
              {hiddenCategoryCount > 0 && (
                <Button
                  variant="text"
                  onClick={() => setShowAllCategories((v) => !v)}
                  className="w-full text-xs normal-case text-text-secondary pl-4 justify-start"
                >
                  {showAllCategories
                    ? LABELS.sitesShowLess
                    : LABELS.sitesShowMore(hiddenCategoryCount)}
                </Button>
              )}
            </CollapsibleFilterSection>
          )}

          {options.length > 1 && (
            <CollapsibleFilterSection
              title={LABELS.options}
              expanded={optionsExpanded}
              onToggle={() => setOptionsExpanded((v) => !v)}
              testId="filter-section-toggle-options"
            >
              <ExpandableFilterList expanded={showAllOptions}>
                <div className="mt-1">
                  {visibleOptions.map((option) => (
                    <FilterButton
                      key={option.key}
                      label={option.label}
                      isActive={optionsFilter.has(option.key) && !isAllOptions}
                      onClick={() => toggleOption(option.key)}
                    />
                  ))}
                </div>
              </ExpandableFilterList>
              {hiddenOptionCount > 0 && (
                <Button
                  variant="text"
                  onClick={() => setShowAllOptions((v) => !v)}
                  className="w-full text-xs normal-case text-text-secondary pl-4 justify-start"
                >
                  {showAllOptions ? LABELS.sitesShowLess : LABELS.sitesShowMore(hiddenOptionCount)}
                </Button>
              )}
            </CollapsibleFilterSection>
          )}

          {sites.length > 1 && (
            <CollapsibleFilterSection
              title={LABELS.location}
              expanded={locationExpanded}
              onToggle={() => setLocationExpanded((v) => !v)}
              testId="filter-section-toggle-location"
            >
              <ExpandableFilterList expanded={showAllSites}>
                <div className="mt-1">
                  {visibleSites.map((site) => (
                    <FilterButton
                      key={site.id}
                      label={site.name}
                      isActive={siteFilter.has(site.id) && !isAllSites}
                      dotColor={site.color}
                      onClick={() => toggleSite(site.id)}
                      testId={`filter-site-${site.id}`}
                    />
                  ))}
                </div>
              </ExpandableFilterList>
              {hiddenCount > 0 && (
                <Button
                  variant="text"
                  onClick={() => setShowAllSites((v) => !v)}
                  className="w-full text-xs normal-case text-text-secondary pl-4 justify-start"
                >
                  {showAllSites ? LABELS.sitesShowLess : LABELS.sitesShowMore(hiddenCount)}
                </Button>
              )}
            </CollapsibleFilterSection>
          )}

          {subcontractors.length > 1 && (
            <CollapsibleFilterSection
              title={LABELS.subcontractor}
              expanded={subcontractorExpanded}
              onToggle={() => setSubcontractorExpanded((v) => !v)}
              testId="filter-section-toggle-subcontractor"
            >
              <ExpandableFilterList expanded={showAllSubcontractors}>
                <div className="mt-1">
                  {visibleSubcontractors.map((subcontractor) => (
                    <FilterButton
                      key={subcontractor.id}
                      label={subcontractor.name}
                      isActive={subcontractorFilter.has(subcontractor.id) && !isAllSubcontractors}
                      onClick={() => toggleSubcontractor(subcontractor.id)}
                      testId={`filter-subcontractor-${subcontractor.id}`}
                    />
                  ))}
                </div>
              </ExpandableFilterList>
              {hiddenSubcontractorCount > 0 && (
                <Button
                  variant="text"
                  onClick={() => setShowAllSubcontractors((v) => !v)}
                  className="w-full text-xs normal-case text-text-secondary pl-4 justify-start"
                >
                  {showAllSubcontractors
                    ? LABELS.sitesShowLess
                    : LABELS.sitesShowMore(hiddenSubcontractorCount)}
                </Button>
              )}
            </CollapsibleFilterSection>
          )}

          {suppliers.length > 1 && (
            <CollapsibleFilterSection
              title={LABELS.supplier}
              expanded={supplierExpanded}
              onToggle={() => setSupplierExpanded((v) => !v)}
              testId="filter-section-toggle-supplier"
            >
              <ExpandableFilterList expanded={showAllSuppliers}>
                <div className="mt-1">
                  {visibleSuppliers.map((supplier) => (
                    <FilterButton
                      key={supplier.id}
                      label={supplier.name}
                      isActive={supplierFilter.has(supplier.id) && !isAllSuppliers}
                      onClick={() => toggleSupplier(supplier.id)}
                      testId={`filter-supplier-${supplier.id}`}
                    />
                  ))}
                </div>
              </ExpandableFilterList>
              {hiddenSupplierCount > 0 && (
                <Button
                  variant="text"
                  onClick={() => setShowAllSuppliers((v) => !v)}
                  className="w-full text-xs normal-case text-text-secondary pl-4 justify-start"
                >
                  {showAllSuppliers
                    ? LABELS.sitesShowLess
                    : LABELS.sitesShowMore(hiddenSupplierCount)}
                </Button>
              )}
            </CollapsibleFilterSection>
          )}
        </div>
      </div>

      <div className="px-2 py-3 border-t border-border flex flex-col gap-1.5">
        {onSave ? (
          <Button variant="contained" fullWidth onClick={onSave} data-testid="save-button">
            {LABELS.saveFilters}
          </Button>
        ) : onClose ? (
          <Button variant="contained" fullWidth onClick={onClose}>
            {LABELS.machinesCount(filteredCount, totalCount)}
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <p className="text-xs text-text-secondary mb-0 mt-0 grow text-center">
              {LABELS.machinesCount(filteredCount, totalCount)}
            </p>
            {onExport && (
              <Tooltip title={LABELS.exportButton}>
                <span>
                  <IconButton
                    onClick={onExport}
                    disabled={exporting}
                    aria-label={LABELS.exportButton}
                    data-testid="export-machines-button"
                  >
                    {exporting ? (
                      <CircularProgress size="1rem" />
                    ) : (
                      <DownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </div>
        )}
        {(onSave || onClose) && onExport && (
          <SpinnerButton
            variant="outlined"
            fullWidth
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            loading={exporting}
            onClick={onExport}
            data-testid="export-machines-button"
          >
            {LABELS.exportButton}
          </SpinnerButton>
        )}
      </div>
    </div>
  );
}
